import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {DashboardWidget} from 'app/components/Dashboard/DashboardWidget';
import {useLensWidgetShareDialog} from 'app/components/Dashboard/hooks/useLensWidgetShareDialog';
import {useMultiFeatureAnalysisData} from 'app/components/Dashboard/hooks/useMultiFeatureAnalysisData';
import {ChartDescription} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/ChartDescription';
import {MultiFeatureAnalysisCategoricalChart} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisCategoricalChart';
import {MultiFeatureAnalysisCategoricalTable} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisCategoricalTable';
import {MultiFeatureAnalysisSkeleton} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisSkeleton';
import {MFAWidgetControls} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/WidgetControls';
import {CategoricalStatResponse, MultifeatureAnalysisConfig} from 'app/components/Dashboard/types';
import {
  getCategoricalData,
  getCategoricalLegendItems,
} from 'app/components/Dashboard/utils/categoricalDataUtils';
import {
  MAX_FEATURES_FOR_MULTI_FEATURE_CHART,
  getAllSortedTimestamps,
  handleDownloadPng,
  useMFADataFilters,
} from 'app/components/Dashboard/utils/chartUtils';
import {
  formatCategoricalAreaTableRows,
  handleDownloadCsv,
} from 'app/components/Dashboard/utils/tableUtils';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganization, getAreaUnit} from 'app/modules/Remote/Organization';
import {ApiOrganizationAreaUnit, TagSetting} from 'app/modules/Remote/Organization';
import {UNIT_AREA_ACRE} from 'app/utils/constants';
import {ImageLayerInfo} from 'app/utils/layers';
import {getLayer} from 'app/utils/layerUtils';

type MultiFeatureCategoricalAnalysisWidgetProps = {
  widgetConfig: MultifeatureAnalysisConfig;
  organization: I.ImmutableOf<ApiOrganization>;
};

type MFACategoricalWidgetViewProps = {
  data?: CategoricalStatResponse;
  widgetConfig: MultifeatureAnalysisConfig;
  isLoading: boolean;
  error?: {message?: string} | null;
  areaUnit?: ApiOrganizationAreaUnit; // defaults to acres if not provided
  orgTags?: TagSetting[]; // optional: used for filtering feature tags
  sharedView?: boolean; // optional: used to determine if the widget is shared
  shareFn?: () => void;
  organization?: I.ImmutableOf<ApiOrganization>;
};

export const MultiFeatureCategoricalAnalysisWidget: React.FC<
  MultiFeatureCategoricalAnalysisWidgetProps
> = ({widgetConfig, organization}) => {
  const {data, isLoading, error} = useMultiFeatureAnalysisData(
    organization.get('id'),
    widgetConfig
  );
  const areaUnit = (organization && getAreaUnit(organization)) || UNIT_AREA_ACRE;
  const orgTags = React.useMemo(() => {
    return organization.get('settings')?.get('feature_tags')?.toJS() || [];
  }, [organization]);

  const {openModal: openShareModal, dialog: shareDialog} = useLensWidgetShareDialog(widgetConfig);

  return (
    <>
      <MultiFeatureCategoricalAnalysisView
        data={data as CategoricalStatResponse}
        widgetConfig={widgetConfig}
        isLoading={isLoading}
        error={error}
        areaUnit={areaUnit}
        orgTags={orgTags}
        shareFn={openShareModal}
        organization={organization}
      />
      {shareDialog}
    </>
  );
};

export const MultiFeatureCategoricalAnalysisView: React.FC<MFACategoricalWidgetViewProps> = ({
  data,
  widgetConfig,
  isLoading,
  error,
  areaUnit = UNIT_AREA_ACRE,
  orgTags = [],
  shareFn,
  organization,
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const pushNotification = usePushNotification();

  const chartHidden = widgetConfig.displayOptions?.chartHidden;
  const [isExpanded, setIsExpanded] = React.useState(chartHidden || !shareFn);
  const [hoveredFeatureId, setHoveredFeatureId] = React.useState<string | null>(null);
  const [displayFeatureIds, setDisplayFeatureIds] = React.useState<Set<string>>(new Set());

  const [currentCategoryId, setCurrentCategoryId] = React.useState<string>(
    widgetConfig.displayOptions?.categoricalKey || 'multiCategory'
  );
  const [dataViewUnit, setDataViewUnit] = React.useState<'area' | 'percentage'>(
    widgetConfig.statAggregation === 'area_percentages' ? 'percentage' : 'area'
  );

  const layer = getLayer(widgetConfig.layerKey);
  const allDates = React.useMemo(() => getAllSortedTimestamps(data), [data]);
  const {filteredData, filterStates, filterActions} = useMFADataFilters(data, orgTags);

  const {tableRows, categories} = React.useMemo(() => {
    if (!filteredData) return {tableRows: [], categories: []};
    const {
      categoryChartData,
      singleCategoryChartDatas,
      categoryChartDataPercentages,
      singleCategoryChartDatasPercentages,
    } = getCategoricalData(
      filteredData,
      widgetConfig.layerKey,
      (layer as ImageLayerInfo).layerLegendMap || {},
      widgetConfig.timeAggregation,
      areaUnit,
      allDates,
      widgetConfig.displayOptions?.includedCategories
    );

    const selectedCategoryView = (() => {
      if (dataViewUnit === 'area') {
        return currentCategoryId === 'multiCategory'
          ? categoryChartData
          : singleCategoryChartDatas[currentCategoryId];
      }
      return currentCategoryId === 'multiCategory'
        ? categoryChartDataPercentages
        : singleCategoryChartDatasPercentages[currentCategoryId];
    })();

    // This might occur if a specific category is set in displayOptions.categoricalKey
    // but the option is configured incorrectly, like using the name of the category
    // instead of the id.
    if (!selectedCategoryView) return {tableRows: [], categories: []};

    return {
      tableRows: formatCategoricalAreaTableRows(selectedCategoryView, allDates),
      categories: Object.keys(singleCategoryChartDatas),
    };
  }, [filteredData, widgetConfig, layer, areaUnit, allDates, dataViewUnit, currentCategoryId]);

  const handleExportPng = React.useCallback(() => {
    if (!filteredData) return;

    handleDownloadPng(
      chartRef.current?.querySelector('canvas'),
      widgetConfig,
      layer,
      isExpanded,
      () => {
        pushNotification({
          message: 'Error downloading chart',
          options: {
            intent: B.Intent.DANGER,
          },
        });
      },
      getCategoricalLegendItems({
        data: filteredData,
        displayFeatureIds,
        layerKey: widgetConfig.layerKey,
        statType: widgetConfig.statAggregation,
        includedCategories: widgetConfig.displayOptions?.includedCategories,
      })
    );
  }, [filteredData, widgetConfig, layer, isExpanded, displayFeatureIds, pushNotification]);

  const exportTableAsCsv = React.useCallback(() => {
    handleDownloadCsv(tableRows, widgetConfig, () => {
      pushNotification({
        message: 'Error exporting table as CSV',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    });
  }, [tableRows, widgetConfig, pushNotification]);

  if (isLoading) {
    return <MultiFeatureAnalysisSkeleton size={isExpanded ? 'large' : 'small'} />;
  }

  return (
    <DashboardWidget
      title={widgetConfig.title}
      size={isExpanded ? 'large' : 'small'}
      controls={
        shareFn && (
          <MFAWidgetControls
            widgetConfig={widgetConfig}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            chartHidden={chartHidden}
            onResize={(direction) => {
              if (direction === 'minimize') {
                setCurrentCategoryId(
                  widgetConfig.displayOptions?.categoricalKey || 'multiCategory'
                );
                setDataViewUnit(
                  widgetConfig.statAggregation === 'area_percentages' ? 'percentage' : 'area'
                );
              }
            }}
            onShare={shareFn}
            onExportPng={handleExportPng}
            onExportCsv={exportTableAsCsv}
          />
        )
      }
    >
      {!filteredData || !data ? (
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="No data"
          description={error?.message ?? 'Error visualizing data'}
        />
      ) : (
        <>
          <ChartDescription
            widgetConfig={widgetConfig}
            layer={layer}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            sharedView={!shareFn}
          />

          {!chartHidden && (
            <div ref={chartRef}>
              <MultiFeatureAnalysisCategoricalChart
                featureStatsResponse={filteredData}
                widgetConfig={widgetConfig}
                isExpanded={isExpanded}
                alwaysShowLegend={displayFeatureIds.size > 0 || !currentCategoryId}
                hoveredFeatureId={hoveredFeatureId}
                currentCategoryId={currentCategoryId}
                dataViewUnit={dataViewUnit}
                areaUnit={areaUnit}
                displayFeatureIds={displayFeatureIds}
                allDates={allDates}
              />
            </div>
          )}

          {(isExpanded || chartHidden) && (
            <MultiFeatureAnalysisCategoricalTable
              rows={tableRows}
              widgetConfig={widgetConfig}
              displayFeatureIds={displayFeatureIds}
              setDisplayFeatureIds={setDisplayFeatureIds}
              setHoveredFeatureId={setHoveredFeatureId}
              allDates={allDates}
              currentCategoryId={currentCategoryId}
              setCurrentCategoryId={setCurrentCategoryId}
              dataViewUnit={dataViewUnit}
              setDataViewUnit={setDataViewUnit}
              organization={organization}
              hasManyFeatures={
                filteredData &&
                Object.keys(filteredData).length > MAX_FEATURES_FOR_MULTI_FEATURE_CHART
              }
              categories={categories}
              filterStates={filterStates}
              filterActions={filterActions}
              isExpanded={isExpanded}
            />
          )}
        </>
      )}
    </DashboardWidget>
  );
};
